import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Avatar, Box, Typography, SxProps, Theme, Chip, TableRow, TableCell } from '@mui/material';

export type FamilyMemberTableRowProps = Readonly<{
  fullName: string;
  email: string;
  role: string;
  avatarUrl?: string;
  isInvite?: boolean;
  button?: React.ReactNode;
}>;

export const FamilyMemberTableRow = ({
  fullName,
  email,
  role,
  avatarUrl,
  isInvite = false,
  button,
}: FamilyMemberTableRowProps) => {
  return (
    <TableRow sx={tableRowSx}>
      <TableCell width="75%">
        <Box sx={avatarNameEmailContainerSx}>
          <Avatar sx={avatarSx} src={avatarUrl} alt={fullName}>
            <Box sx={fallbackAvatarSx}>
              <AppIcon
                iconName="User01"
                diameter="24px"
                color={'Colors/Foreground/fg-quaternary'}
              />
            </Box>
          </Avatar>
          <Box sx={nameEmailContainerSx}>
            <Box sx={nameContainerSx}>
              <Typography variant="text-sm-medium" color="Colors/Text/text-primary" sx={nameSx}>
                {fullName}
              </Typography>
              {isInvite && <Chip label="Pending" size="small" sx={chipSx} />}
            </Box>
            <Typography variant="text-sm-regular" color="Colors/Text/text-tertiary" sx={emailSx}>
              {email}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell sx={roleCellSx}>{role}</TableCell>
      <TableCell>{button}</TableCell>
    </TableRow>
  );
};

const avatarSx: SxProps<Theme> = ({ backrs }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '200px',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}08`, // 8% opacity
  flexShrink: 0,
});

const fallbackAvatarSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  borderRadius: '200px',
});

const avatarNameEmailContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const nameEmailContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
};

const nameContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const nameSx: SxProps<Theme> = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const emailSx: SxProps<Theme> = ({ backrs }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Gray/100'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const tableRowSx: SxProps<Theme> = ({ backrs }) => ({
  '& > td': {
    paddingBlock: backrs.spacing['spacing-xl'],
  },
  '& > td:first-of-type': {
    paddingLeft: backrs.spacing['spacing-3xl'],
  },
  '& > td:last-of-type': {
    paddingRight: backrs.spacing['spacing-3xl'],
  },
});

const roleCellSx: SxProps<Theme> = ({ backrs }) => ({
  textTransform: 'capitalize',
  color: backrs.colors['Colors/Text/text-tertiary'],
});
